import * as React from 'react'
import { Link } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { TableHead } from '@mui/material'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import ArrowRightIcon from '../../../../assets/icons/Arrows-chevron/Arrow-Right.svg'
import ConfirmDelete from '../../../../components/dialogs/ConfirmDelete'
import { SubjectState } from '../../../../features/types'
import AddSubjectDialog from './AddSubjectDialog'
import { DEFAULT_PAGE_SIZE } from '../../../../utils/constants'

interface Props {
    subjectsData?: SubjectState[]
    onDelete?: (subject: number) => void
}

const SubjectsTable: React.FC<Props> = ({ subjectsData, onDelete }) => {
    const { formatMessage } = useIntl()
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGE_SIZE)

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0
            ? Math.max(0, (1 + page) * rowsPerPage - subjectsData.length)
            : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                            <TableCell>
                                {formatMessage({ id: 'subjectName' })} (kz / ru)
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'subjectCode' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'topics' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'edit' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'delete' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(rowsPerPage > 0
                            ? subjectsData.slice(
                                  page * rowsPerPage,
                                  page * rowsPerPage + rowsPerPage,
                              )
                            : subjectsData
                        ).map((row) => (
                            <TableRow key={row.id}>
                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    {row.titleKz} / {row.titleRu}
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    {row.code || '-'}
                                </TableCell>

                                <TableCell
                                    style={{ width: 60 }}
                                    align="right"
                                >
                                    <Link
                                        to={'topics/' + row.id}
                                        state={{ subject: row }}
                                    >
                                        <IconButton
                                            sx={{ background: '#f3f4f5' }}
                                        >
                                            <img
                                                src={ArrowRightIcon}
                                                alt="edit"
                                            />
                                        </IconButton>
                                    </Link>
                                </TableCell>

                                <TableCell
                                    style={{ width: 60 }}
                                    align="right"
                                >
                                    <AddSubjectDialog
                                        editMode={true}
                                        editSubject={row}
                                    />
                                </TableCell>

                                <TableCell
                                    style={{ width: 60 }}
                                    align="right"
                                >
                                    <ConfirmDelete
                                        confirmAction={() => onDelete(row.id)}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}

                        {emptyRows > 0 && (
                            <TableRow style={{ height: 53 * emptyRows }}>
                                <TableCell colSpan={6} />
                            </TableRow>
                        )}
                    </TableBody>

                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[
                                    5,
                                    10,
                                    25,
                                    { label: 'All', value: -1 },
                                ]}
                                colSpan={5}
                                count={subjectsData.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                SelectProps={{
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    native: true,
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                                sx={{ border: 0 }}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    )
}

export default SubjectsTable
